import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import cc from 'classcat';
import { CommandNames } from 'utils/constants/swarm/swarm-command-names';
import { SwarmSuccessMessages } from 'utils/constants/swarm/swarm-success-messages';
import { showToastSuccess } from 'utils/swarm/swarm-success-handler';
import { UserOptInsResponse } from 'interfaces/account-settings';
import { getPlayerOptIns } from 'services/account-settings';
import { AFTER_SIGN_IN_ACTION_TYPES, AfterSignIn } from 'services/afterSignIn';
import { swarmCommand } from 'services/get-swarm-data';
import { addQuery } from 'components/QueryRoute';
import { setShouldGetUserOptIns, setUserOptIns } from 'store/actions';
import {
  getIsLoggedIn,
  getUser,
  getUserOptIns
} from 'store/selectors/user-data';
import './index.less';

type TOptInProps = {
  configs: {
    optInName: string;
    optInButtonStyle: string;
    optOutButtonStyle: string;
  };
};

const optInDefaultProps = {
  configs: {
    optInName: 'test-1111',
    optInButtonStyle: 'btn1',
    optOutButtonStyle: 'btn1'
  }
};

export const OptIn = (props: TOptInProps = optInDefaultProps): ReactElement => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const isLoggedIn = useSelector(getIsLoggedIn);
  const userOptIns = useSelector(getUserOptIns);
  const user = useSelector(getUser);

  const [btnLoading, setBtnLoading] = useState(false);

  const { optInName, optInButtonStyle, optOutButtonStyle } = props.configs;

  const shouldOptOut = useMemo(() => {
    return userOptIns?.find(optIn => optIn.Code === optInName);
  }, [userOptIns]);

  const getOptIns = useCallback(() => {
    getPlayerOptIns(
      (resp: UserOptInsResponse) => {
        if (resp && resp.details) {
          dispatch(setUserOptIns(resp.details));
          dispatch(setShouldGetUserOptIns(true));
          setBtnLoading(false);
        }
      },
      () => {
        setBtnLoading(false);
      }
    );
  }, []);

  const optAction = useCallback((isOptIn?: boolean) => {
    return new Promise(resolve => {
      setBtnLoading(true);

      swarmCommand(
        CommandNames[isOptIn ? 'CLIENT_OPT_IN' : 'CLIENT_OPT_OUT'],
        { code: optInName },
        (data: Record<string, number>) => {
          if (data.result === 0) {
            showToastSuccess(
              t(
                `account.${
                  SwarmSuccessMessages[isOptIn ? 'OPT_IN' : 'OPT_OUT']
                }`
              )
            );
            getOptIns();
            resolve(true);
          }
        },
        null,
        () => {
          setBtnLoading(false);
          getOptIns();
          resolve(true);
        }
      );
    });
  }, []);

  const buttonClickHandler = useCallback(async () => {
    if (isLoggedIn) {
      if (shouldOptOut) {
        await optAction();
      } else {
        await optAction(true);
      }
    } else {
      dispatch(setShouldGetUserOptIns(false));
      history.push(addQuery({ accounts: '*', login: '*' }));
      const afterSignIn = new AfterSignIn();

      const optActionPromise = async () => {
        return new Promise(resolve => {
          optAction(true).finally(() => {
            resolve(true);
          });
        });
      };

      afterSignIn.appendAction(
        AFTER_SIGN_IN_ACTION_TYPES.OPT_IN_ACTION,
        optActionPromise
      );
    }
  }, [isLoggedIn, shouldOptOut]);

  useEffect(() => {
    dispatch(setShouldGetUserOptIns(true));
  }, []);

  return (
    <div
      className={cc([
        'btn',
        {
          [optInButtonStyle]: !shouldOptOut,
          [optOutButtonStyle]: shouldOptOut,
          'btn-loading': btnLoading || user.pending,
          'btn-optOut': shouldOptOut
        }
      ])}
      onClick={buttonClickHandler}
    >
      <span>{shouldOptOut ? t(`account.optOut`) : t(`account.optIn`)}</span>
    </div>
  );
};
